import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { useAuth } from '@/lib/auth';
import { routeForNotification } from '@/lib/notificationRoutes';
import { toast } from 'sonner';
import {
  Bell, CheckCheck, DollarSign, Send, AlertTriangle, Package, Info,
  MessageSquare, ShieldAlert, UserCheck, ShoppingBag, Tag, Receipt,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─── TYPE_META ────────────────────────────────────────────────────────────
 * Icono + etiqueta + color por `type`. Cualquier tipo nuevo cae en
 * `META_FOR` → `generic`, por eso el helper nunca lanza aunque el catálogo
 * crezca sin coordinación con el frontend.
 */
const TYPE_META = {
  // Payouts seller
  'payout.scheduled':        { label: 'Retiro programado',    icon: DollarSign,    cls: 'text-indigo-600' },
  'payout.sent':             { label: 'Retiro enviado',       icon: Send,          cls: 'text-amber-600' },
  'payout.completed':        { label: 'Pago completado',      icon: CheckCheck,    cls: 'text-emerald-600' },
  'payout.held':             { label: 'Retiro bloqueado',     icon: AlertTriangle, cls: 'text-rose-600' },
  'seller.payout.processed': { label: 'Retiro procesado',     icon: CheckCheck,    cls: 'text-emerald-600' },
  'seller.payout.rejected':  { label: 'Retiro rechazado',     icon: AlertTriangle, cls: 'text-rose-600' },
  'seller.payout.requested': { label: 'Retiro solicitado',    icon: DollarSign,    cls: 'text-indigo-600' },
  // Payouts admin
  'admin.payout.stuck':           { label: 'Payout atascado',           icon: AlertTriangle, cls: 'text-rose-600' },
  'admin.payout.retry_exhausted': { label: 'Payout retry agotado',      icon: AlertTriangle, cls: 'text-rose-700' },
  // Pedidos / factura / refund
  'order':                   { label: 'Pedido',               icon: Package,       cls: 'text-blue-600' },
  'buyer.order.paid':        { label: 'Pedido pagado',        icon: Package,       cls: 'text-blue-600' },
  'seller.order.paid':       { label: 'Venta pagada',         icon: ShoppingBag,   cls: 'text-blue-600' },
  'buyer.invoice.available': { label: 'Factura disponible',   icon: Receipt,       cls: 'text-emerald-700' },
  'buyer.refund.issued':     { label: 'Reembolso emitido',    icon: Receipt,       cls: 'text-amber-700' },
  // Productos / promos
  'seller.product.out_of_stock': { label: 'Producto sin stock', icon: Package,  cls: 'text-rose-500' },
  'seller.promo.active':         { label: 'Promoción activa',   icon: Tag,      cls: 'text-emerald-600' },
  'seller.promo.expiring':       { label: 'Promoción por vencer', icon: Tag,    cls: 'text-amber-600' },
  'seller.promo.expired':        { label: 'Promoción vencida',  icon: Tag,      cls: 'text-slate-500' },
  'buyer.promo.offer':           { label: 'Oferta disponible',  icon: Tag,      cls: 'text-emerald-600' },
  // Planes / activación
  'seller.activation.paid':     { label: 'Activación pagada',      icon: CheckCheck,    cls: 'text-emerald-600' },
  'seller.activation.expiring': { label: 'Activación por vencer',  icon: AlertTriangle, cls: 'text-amber-600' },
  'seller.activation.expired':  { label: 'Activación vencida',     icon: AlertTriangle, cls: 'text-rose-600' },
  'seller.plan.activated':      { label: 'Plan activado',          icon: CheckCheck,    cls: 'text-emerald-600' },
  'seller.plan.expiring':       { label: 'Plan por vencer',        icon: AlertTriangle, cls: 'text-amber-600' },
  'seller.plan.expired':        { label: 'Plan vencido',           icon: AlertTriangle, cls: 'text-rose-600' },
  // Vendedor / onboarding
  'seller.approved':                   { label: 'Vendedor aprobado',    icon: UserCheck, cls: 'text-emerald-600' },
  'seller.rejected':                   { label: 'Vendedor rechazado',   icon: UserCheck, cls: 'text-rose-600' },
  'admin.seller.onboarding_submitted': { label: 'Nuevo vendedor',       icon: UserCheck, cls: 'text-blue-600' },
  // Chat
  'buyer.chat.new':   { label: 'Nuevo mensaje', icon: MessageSquare, cls: 'text-blue-600' },
  // PQR
  'admin.pqr.new':    { label: 'Nuevo PQR',     icon: AlertTriangle, cls: 'text-amber-700' },
  // Seguridad
  'security.alert':                 { label: 'Alerta de seguridad', icon: ShieldAlert, cls: 'text-rose-600' },
  'security.account.suspended':     { label: 'Cuenta suspendida',   icon: ShieldAlert, cls: 'text-rose-700' },
  'security.account.reactivated':   { label: 'Cuenta reactivada',   icon: ShieldAlert, cls: 'text-emerald-600' },
  'security.password_changed':      { label: 'Contraseña cambiada', icon: ShieldAlert, cls: 'text-blue-600' },
  'security.password_reset':        { label: 'Reset de contraseña', icon: ShieldAlert, cls: 'text-blue-600' },
  // Fallback
  'generic':          { label: 'General',       icon: Info,          cls: 'text-slate-500' },
};

const META_FOR = (t) => {
  if (!t) return TYPE_META.generic;
  if (TYPE_META[t]) return TYPE_META[t];
  if (t.startsWith('payout.') || t.startsWith('seller.payout.')) return TYPE_META['payout.scheduled'];
  if (t.startsWith('admin.payout.')) return TYPE_META['admin.payout.stuck'];
  if (t.startsWith('security.')) return TYPE_META['security.alert'];
  return TYPE_META.generic;
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useSEO({
    title: 'Notificaciones | AutoMarket Colombia',
    description: 'Historial de notificaciones — pagos, pedidos, alertas.',
    canonical: '/notificaciones',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (typeFilter !== 'all') params.type_prefix = typeFilter;
      if (unreadOnly) params.unread_only = true;
      const { data } = await api.get('/notifications', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [typeFilter, unreadOnly]); // eslint-disable-line

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((n) => n.id)));
  };

  const markSelected = async () => {
    if (selected.size === 0) return;
    try {
      await api.post('/notifications/read-bulk', { ids: Array.from(selected) });
      toast.success(`${selected.size} notificación(es) marcada(s) como leída(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      const { data } = await api.post('/notifications/read-all');
      toast.success(`${data.modified} notificación(es) marcada(s) como leída(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  /** Click en la fila: marca como leída y navega al destino, o solo marca. */
  const openNotification = async (n) => {
    const target = routeForNotification(n, user?.role);
    // Marcar leída (silenciosa si falla; igual se navega).
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* silent */ }
    }
    if (target) {
      navigate(target);
    } else {
      toast.success('Notificación marcada como leída');
      load();
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Mi cuenta</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 inline-flex items-center gap-2">
              <Bell className="h-6 w-6" /> Notificaciones
            </h1>
            <p className="text-sm text-slate-500 mt-1">{unread} sin leer · {total} en total</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="notif-mark-all-btn"
              onClick={markAll}
              disabled={unread === 0}
              variant="outline"
              className="rounded-none"
            >
              <CheckCheck className="mr-1 h-4 w-4" /> Marcar todas leídas
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px]">
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Filtrar por tipo</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="notif-type-filter" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="payout">Retiros (payouts)</SelectItem>
                <SelectItem value="order">Pedidos</SelectItem>
                <SelectItem value="admin">Alertas admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer h-10">
            <Checkbox
              data-testid="notif-unread-only"
              checked={unreadOnly}
              onCheckedChange={(v) => setUnreadOnly(!!v)}
            />
            <span className="text-sm text-slate-700">Sólo sin leer</span>
          </label>
        </div>

        {/* Bulk actions bar */}
        {items.length > 0 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <Checkbox
                data-testid="notif-select-all"
                checked={selected.size === items.length && items.length > 0}
                onCheckedChange={selectAll}
              />
              <span className="text-slate-600">Seleccionar todas</span>
            </label>
            {selected.size > 0 && (
              <Button
                data-testid="notif-mark-bulk-btn"
                onClick={markSelected}
                size="sm"
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                Marcar {selected.size} como leída{selected.size > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {/* List */}
        <div className="mt-4 border border-slate-200 bg-white divide-y divide-slate-100" data-testid="notif-list">
          {loading && (<div className="p-8 text-center text-slate-500">Cargando…</div>)}
          {!loading && items.length === 0 && (
            <div className="p-16 text-center text-slate-500">
              <Bell className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3">{unreadOnly ? 'No tienes notificaciones sin leer.' : 'Aún no tienes notificaciones.'}</p>
            </div>
          )}
          {items.map((n) => {
            const meta = META_FOR(n.type);
            const Icon = meta.icon;
            const target = routeForNotification(n, user?.role);
            return (
              <div
                key={n.id}
                data-testid={`notif-row-${n.id}`}
                className={`p-4 flex items-start gap-3 ${n.read ? 'bg-white' : 'bg-blue-50/40'} ${target ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-pointer'}`}
                onClick={(e) => {
                  // Ignorar clicks sobre el checkbox o el botón explícito.
                  if (e.target.closest('[data-notif-stop-propagation]')) return;
                  openNotification(n);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') openNotification(n); }}
              >
                <span data-notif-stop-propagation onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.has(n.id)}
                    onCheckedChange={() => toggle(n.id)}
                    data-testid={`notif-select-${n.id}`}
                  />
                </span>
                <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${meta.cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">{n.title}</div>
                    <span className="text-[11px] text-slate-400 shrink-0">{formatDate(n.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">{n.body}</div>
                  <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap items-center gap-3">
                    <span className="uppercase tracking-wider font-bold" data-testid={`notif-badge-${n.id}`}>{meta.label}</span>
                    {!n.read && (
                      <span data-notif-stop-propagation onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid={`notif-mark-${n.id}`}
                          onClick={() => markOne(n.id)}
                          className="text-slate-500 hover:underline"
                        >
                          Marcar leída
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
